// Candidate sections for the Shift Detail panel: Eligible from Home Unit / Available from Other
// Units / Not Eligible from Home Unit, each a Collapse panel with a count badge and the eligibility
// sublabel. Open shifts default the home section open (auto-opening Other Units when home has no
// eligibles); assigned shifts start fully collapsed — the scheduler is reviewing, not filling.
// Buckets are the recomputed candidate set; null while loading or for an unknown/foreign shift.
import React from 'react';
import {
  Collapse,
  Gap,
  Gutter,
  LoadingWrapper,
  Tag,
  Text,
} from '@common/components';
import type { ScheduleDragStateType } from '@load-balancing/types';
import type { ScheduleCandidateBucketsType } from './_schedule-shift-detail-panel-service';
import { defaultCandidateSections } from './_schedule-shift-detail-panel-service';
import { ScheduleShiftDetailCandidateRow } from './_schedule-shift-detail-candidate-row';

function SectionTitle({ label, count }: { label: string; count: number }) {
  return (
    <Gap size="sm" align="center">
      <Text size="lg" weight="medium" color="midnightDarkest">
        {label}
      </Text>
      <Tag color="steelLighter">{count}</Tag>
    </Gap>
  );
}

export function ScheduleShiftDetailCandidates({
  buckets,
  loading,
  isOpenSlot,
  coverage,
  durationHours,
  assigning = false,
  onAssign,
  onWorkerSelect,
  onWorkerDragStart,
  onWorkerDragEnd,
}: {
  /** Enriched candidate buckets; null while loading or for an unknown/foreign shift. */
  buckets: ScheduleCandidateBucketsType | null;
  loading: boolean;
  /** Open slot: default the home section open and show the assign KPI delta on rows. */
  isOpenSlot: boolean;
  /** Cell coverage base for the KPI delta; passed through to eligible rows. */
  coverage: { before: number; target: number } | null;
  /** Shift length in hours, for the per-candidate FTE delta. */
  durationHours: number;
  /** An assignShift is in flight — disable every Assign button until it settles. */
  assigning?: boolean;
  onAssign?: (memberId: string) => void;
  onWorkerSelect?: (memberId: string) => void;
  onWorkerDragStart?: (state: ScheduleDragStateType) => void;
  onWorkerDragEnd?: () => void;
}) {
  if (loading || !buckets) {
    return (
      <LoadingWrapper loading={loading}>
        <Text size="md" color="midnightLighter">
          {loading ? 'Loading candidates…' : 'No candidate data for this shift'}
        </Text>
      </LoadingWrapper>
    );
  }

  const { homeEligible, homeNotEligible, crossEligible } = buckets;
  const defaultActiveKey = defaultCandidateSections();

  return (
    <Collapse defaultActiveKey={defaultActiveKey}>
      <Collapse.Panel
        panelKey="home"
        title={
          <SectionTitle
            label="Eligible from Home Unit"
            count={homeEligible.length}
          />
        }
      >
        <Gutter direction="bottom" size="md">
          <Gap vertical size="md" align="stretch" fullWidth>
            <Text size="md" secondary block>
              Role, unit, and qualification matched ∙ No conflicts or PTO
            </Text>
            {homeEligible.length === 0 ? (
              <Gap vertical size="xxs" align="start">
                <Text size="md" color="midnightLighter">
                  No eligible candidates for this shift
                </Text>
                {homeNotEligible.length > 0 && (
                  <Text size="sm" secondary>
                    All available staff are at FTE or have scheduling conflicts
                  </Text>
                )}
              </Gap>
            ) : (
              homeEligible.map(({ candidate, member }) => (
                <ScheduleShiftDetailCandidateRow
                  key={member.id}
                  candidate={candidate}
                  member={member}
                  showKpi={isOpenSlot}
                  coverage={coverage}
                  durationHours={durationHours}
                  assigning={assigning}
                  onAssign={isOpenSlot ? onAssign : undefined}
                  onWorkerSelect={onWorkerSelect}
                  onWorkerDragStart={onWorkerDragStart}
                  onWorkerDragEnd={onWorkerDragEnd}
                />
              ))
            )}
          </Gap>
        </Gutter>
      </Collapse.Panel>

      {crossEligible.length > 0 && (
        <Collapse.Panel
          panelKey="cross"
          title={
            <SectionTitle
              label="Available from Other Units"
              count={crossEligible.length}
            />
          }
        >
          <Gutter direction="bottom" size="md">
            <Gap vertical size="md" align="stretch" fullWidth>
              {crossEligible.map(({ candidate, member }) => (
                <ScheduleShiftDetailCandidateRow
                  key={member.id}
                  candidate={candidate}
                  member={member}
                  showKpi={isOpenSlot}
                  coverage={coverage}
                  durationHours={durationHours}
                  assigning={assigning}
                  onAssign={isOpenSlot ? onAssign : undefined}
                  onWorkerSelect={onWorkerSelect}
                  onWorkerDragStart={onWorkerDragStart}
                  onWorkerDragEnd={onWorkerDragEnd}
                />
              ))}
            </Gap>
          </Gutter>
        </Collapse.Panel>
      )}
      {homeNotEligible.length > 0 && (
        <Collapse.Panel
          panelKey="not-eligible"
          title={
            <SectionTitle
              label="Not Eligible from Home Unit"
              count={homeNotEligible.length}
            />
          }
        >
          <Gutter direction="bottom" size="md">
            <Gap vertical size="md" align="stretch" fullWidth>
              {homeNotEligible.map(({ candidate, member }) => (
                // Informational only: not-eligible home workers show their blocking
                // reasons and "Xh left", but carry no assign affordance (per design —
                // capacity/eligibility blocks are hard, not overridable).
                <ScheduleShiftDetailCandidateRow
                  key={member.id}
                  candidate={candidate}
                  member={member}
                  onWorkerSelect={onWorkerSelect}
                  onWorkerDragStart={onWorkerDragStart}
                  onWorkerDragEnd={onWorkerDragEnd}
                />
              ))}
            </Gap>
          </Gutter>
        </Collapse.Panel>
      )}
    </Collapse>
  );
}
